import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  LayoutDashboard, Image, Calendar, PenSquare,
  Link2, Settings,
  ChevronDown, LogOut, Plus, Building2, Menu, ChevronLeft,
  Sun, Moon, ArrowLeft, ListChecks
} from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const LOGO_DARK = '/stp-logo.png';
const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_stellar-crm-hub/artifacts/pvd4h9i1_White%20and%20Yellow%20with%20name.png';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/crm' },
  { icon: Image, label: 'Media Library', path: '/media' },
  { icon: Calendar, label: 'Content Calendar', path: '/calendar' },
  { icon: PenSquare, label: 'Post Composer', path: '/compose' },
  { icon: ListChecks, label: 'Post Queue', path: '/queue' },
  { icon: Link2, label: 'Integrations', path: '/integrations' },
  { icon: Settings, label: 'Settings', path: '/crm/settings' },
];
// 2026-07 UI simplification — Approvals, Client Groups, Analytics, Reports,
// Ad Manager, AI Assistant, Team and Admin are hidden from the Social CRM nav
// (routes + backend stay intact; pages remain reachable by direct URL).

// Role-based navigation filtering
const ROLE_NAV_ACCESS = {
  super_admin: navItems.map(i => i.path),
  owner: navItems.map(i => i.path),
  admin: navItems.map(i => i.path),
  manager: ['/crm', '/media', '/calendar', '/compose', '/queue', '/integrations', '/crm/settings'],
  team: ['/crm', '/media', '/calendar', '/compose', '/queue', '/crm/settings'],
  client: ['/crm', '/calendar'],
};

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuth();
  const { workspaces, currentWorkspace, switchWorkspace } = useWorkspace();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  // Use workspace-level role for nav filtering, fall back to user.role for super_admin
  const { workspaceRole } = useWorkspace();
  const effectiveRole = user?.role === 'super_admin' ? 'super_admin' : (workspaceRole || user?.role || 'team');
  const allowedPaths = ROLE_NAV_ACCESS[effectiveRole] || ROLE_NAV_ACCESS['team'];
  const filteredNav = navItems.filter(item => allowedPaths.includes(item.path));
  const logo = theme === 'dark' ? LOGO_DARK : LOGO_LIGHT;

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={`h-screen flex flex-col bg-card border-r border-border transition-all duration-200 ${
          collapsed ? 'w-[72px]' : 'w-[280px]'
        }`}
        data-testid="sidebar"
      >
        {/* Logo + Collapse */}
        <div className="flex items-center justify-between px-3 h-14 border-b border-border">
          {!collapsed && (
            <div className="flex items-center gap-2 overflow-hidden">
              <img src={logo} alt="Stellar Productions" className="h-8 w-8 object-contain rounded" />
              <div className="min-w-0">
                <span className="font-display font-semibold text-sm text-gold block truncate">Stellar CRM</span>
              </div>
            </div>
          )}
          {collapsed && (
            <img src={logo} alt="SP" className="h-7 w-7 object-contain rounded mx-auto" />
          )}
          {!collapsed && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="h-8 w-8 shrink-0"
              data-testid="sidebar-toggle"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Workspace Switcher */}
        {!collapsed && (
          <div className="px-3 py-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-between h-10 px-3"
                  data-testid="workspace-switcher-trigger"
                >
                  <div className="flex items-center gap-2 truncate">
                    <Building2 className="h-4 w-4 shrink-0 text-gold" />
                    <span className="truncate text-sm font-medium">
                      {currentWorkspace?.name || 'Select Workspace'}
                    </span>
                  </div>
                  <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[252px]">
                {workspaces.map(ws => (
                  <DropdownMenuItem
                    key={ws.id}
                    onClick={() => switchWorkspace(ws)}
                    className={ws.id === currentWorkspace?.id ? 'bg-secondary' : ''}
                  >
                    <Building2 className="h-4 w-4 mr-2" />
                    {ws.name}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                {/* Phase 3B Batch 1 T-CRIT-1 — workspace creation is Owner-only. */}
                {(user?.role === 'super_admin' || workspaceRole === 'owner') && (
                  <DropdownMenuItem onClick={() => navigate('/settings?tab=workspace&new=true')} data-testid="sidebar-new-workspace-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    New Workspace
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        <Separator />

        {/* Switch Tool */}
        <div className="px-3 py-1">
          <Button variant="ghost" size="sm" className={`w-full justify-start text-xs text-muted-foreground hover:text-foreground ${collapsed ? 'px-2 justify-center' : ''}`} onClick={() => navigate('/')} data-testid="switch-tool-btn">
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
            {!collapsed && 'Switch Tool'}
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-2 py-2">
          <nav className="space-y-1">
            {filteredNav.map(item => {
              const isActive = location.pathname === item.path ||
                (item.path !== '/crm' && location.pathname.startsWith(item.path));
              const NavButton = (
                <Button
                  key={item.path}
                  variant={isActive ? 'secondary' : 'ghost'}
                  className={`w-full ${collapsed ? 'justify-center px-2' : 'justify-start px-3'} h-9 ${
                    isActive ? 'bg-secondary text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className={`h-4 w-4 ${collapsed ? '' : 'mr-3'} shrink-0 ${isActive ? 'text-gold' : ''}`} />
                  {!collapsed && <span className="text-sm truncate">{item.label}</span>}
                </Button>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>{NavButton}</TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                );
              }
              return NavButton;
            })}
          </nav>
        </ScrollArea>

        <Separator />

        {/* Theme Toggle + User Menu */}
        <div className="p-3 space-y-2">
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            className={`w-full ${collapsed ? 'justify-center px-2' : 'justify-start px-3'} h-9 text-muted-foreground hover:text-foreground`}
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
          >
            {theme === 'dark' ? (
              <Sun className={`h-4 w-4 ${collapsed ? '' : 'mr-3'} shrink-0`} />
            ) : (
              <Moon className={`h-4 w-4 ${collapsed ? '' : 'mr-3'} shrink-0`} />
            )}
            {!collapsed && <span className="text-sm">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={`w-full ${collapsed ? 'justify-center px-2' : 'justify-start px-2'} h-10`}
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-gold/20 text-gold text-xs">{initials}</AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <div className="ml-2 text-left truncate">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate capitalize">{effectiveRole} &middot; {user?.email}</p>
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="h-4 w-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive">
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </TooltipProvider>
  );
}
